<section class="widget">
  <h2>{{ title }}</h2>
  <div class="explanation">
    <strong>How to use this widget</strong>
    <p>
      Findings load automatically using the backend's flagged result set. To
      search a period, select both a start and end date/time, then choose
      <b>Apply filters</b>. Dates refer to the transcript analysis timestamp and
      are converted to ISO-8601 before the request is sent. Use
      <b>Refresh</b> to repeat the current query.
    </p>
  </div>
  <app-date-range-filter
    #dateFilter
    (apply)="
      applyFilters(
        dateFilter.startDateIso,
        dateFilter.endDateIso,
        dateFilter.sk,
        dateFilter.limit
      )
    "
    (clear)="clear.emit()"
    (refresh)="refresh.emit()"
  ></app-date-range-filter>
  <div class="summary-container">
    <div class="badges">
      <span
        >PII findings <b>{{ count('pii') }}</b></span
      >
      <span
        >Risky advice <b>{{ count('riskyAdvice') }}</b></span
      >
      <span
        >Smoke/fire findings <b>{{ count('smoke') }}</b></span
      >
    </div>
    @if (!loading && !error && findings.length) {
      <<<<<<< HEAD
      <div class="widget-actions">
        <button
          mat-stroked-button
          type="button"
          class="toggle-results-btn"
          (click)="toggleShowResults()"
          [attr.aria-expanded]="showResults"
        >
          <mat-icon>{{ showResults ? 'expand_less' : 'expand_more' }}</mat-icon>
          {{ showResults ? 'Hide results' : 'Show results' }}
          ({{ findings.length }})
        </button>
        <button
          mat-stroked-button
          type="button"
          class="hide-completed-btn"
          (click)="toggleHideCompleted()"
          [attr.aria-pressed]="hideCompleted"
        >
          <mat-icon>{{
            hideCompleted ? 'visibility' : 'visibility_off'
          }}</mat-icon>
          {{ hideCompleted ? 'Show completed' : 'Hide completed' }}
          ({{ completedCount }})
        </button>
      </div>
      =======
      <button
        mat-stroked-button
        type="button"
        class="toggle-results-btn"
        (click)="toggleShowResults()"
        [attr.aria-expanded]="showResults"
      >
        <mat-icon>{{ showResults ? 'expand_less' : 'expand_more' }}</mat-icon>
        {{ showResults ? 'Hide results' : 'Show results' }}
        ({{ findings.length }})
      </button>
      >>>>>>> origin/main
    }
  </div>

  @if (loading) {
    <app-loading-state></app-loading-state>
  } @else if (error) {
    <app-error-state [message]="error"></app-error-state>
  } @else if (!findings.length) {
    <app-empty-state></app-empty-state>
  } @else if (showResults && !visibleFindings.length) {
    <app-empty-state
      message="All findings in this range are marked as review complete."
    ></app-empty-state>
  } @else if (showResults) {
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th class="expand-col" aria-label="Expand finding details"></th>
            <th>Transcript timestamp</th>
            @if (voice) {
              <th>Connection / voice session</th>
            }
            <th>Customer ID</th>
            <th>Session ID</th>
            <th>Plan number</th>
            <th>Product type</th>
            <th>Brand</th>
            <th>PII</th>
            <th>Risky advice</th>
            <th>Smoke/fire</th>
            <th>Guardrail</th>
            <th>Engineer</th>
            <th>Human</th>
            <th>Negative</th>
            <th>Positive</th>
            <th>Multiple instructions</th>
            <th>Status</th>
            <th>Analysed at</th>
            <th>Review status</th>
          </tr>
        </thead>
        <tbody>
          @for (
            item of visibleFindings;
            track item.sk || item.sessionId || $index
          ) {
            <tr
              class="data-row"
              [class.expanded]="isExpanded(item)"
              (click)="toggleExpand(item)"
              tabindex="0"
              role="button"
              [attr.aria-expanded]="isExpanded(item)"
              (keydown.enter)="toggleExpand(item)"
              (keydown.space)="$event.preventDefault(); toggleExpand(item)"
            >
              <td class="expand-col" (click)="$event.stopPropagation()">
                <button
                  mat-icon-button
                  type="button"
                  class="expand-btn"
                  (click)="toggleExpand(item)"
                  [attr.aria-expanded]="isExpanded(item)"
                  [attr.aria-label]="
                    isExpanded(item)
                      ? 'Collapse row details'
                      : 'Expand row details'
                  "
                >
                  <mat-icon>{{
                    isExpanded(item) ? 'expand_less' : 'expand_more'
                  }}</mat-icon>
                </button>
              </td>
              <td>{{ item.sk | date: 'medium' }}</td>
              @if (voice) {
                <td>
                  {{ item.connectionId || item.pk }}<br /><small>{{
                    item.voiceSessionId || item.sessionId || '—'
                  }}</small>
                </td>
              }
              <td>{{ item.customerId || '—' }}</td>
              <td>{{ item.sessionId || '—' }}</td>
              <td>{{ item.planNumber || '—' }}</td>
              <td>{{ item.productType || '—' }}</td>
              <td>{{ item.brand || '—' }}</td>
              <td>
                <app-compliance-badge
                  [label]="badge(item, 'pii').label"
                  [tone]="badge(item, 'pii').tone"
                ></app-compliance-badge>
              </td>
              <td>
                <app-compliance-badge
                  [label]="badge(item, 'risky').label"
                  [tone]="badge(item, 'risky').tone"
                ></app-compliance-badge>
              </td>
              <td>
                <app-compliance-badge
                  [label]="badge(item, 'smoke').label"
                  [tone]="badge(item, 'smoke').tone"
                ></app-compliance-badge>
              </td>
              <td>
                <app-compliance-badge
                  [label]="badge(item, 'guardrail').label"
                  [tone]="badge(item, 'guardrail').tone"
                ></app-compliance-badge>
              </td>
              <td>{{ item.engineerRequestCount }}</td>
              <td>{{ item.humanRequestCount }}</td>
              <td>{{ item.negativeSentimentCount }}</td>
              <td>{{ item.positiveSentimentCount }}</td>
              <td>{{ item.multipleInstructionsCount }}</td>
              <td>{{ item.analysisStatus }}</td>
              <td>
                {{ item.analysedAt ? (item.analysedAt | date: 'medium') : '—' }}
              </td>
              <td (click)="$event.stopPropagation()">
                <app-review-status-control
                  [reviewStatus]="item.reviewStatus"
                  [reviewedBy]="item.reviewedBy"
                  [reviewedAt]="item.reviewedAt"
                  [loading]="isUpdatingReview(item)"
                  (toggled)="toggleReviewStatus(item)"
                ></app-review-status-control>
              </td>
            </tr>
            @if (isExpanded(item)) {
              <tr class="detail-row">
                <td [attr.colspan]="voice ? 20 : 19">
                  <div class="accordion-content">
                    <section class="conversation-history">
                      <h3>Conversation</h3>
                      @if (!messageHistory(item).length) {
                        <p class="no-history">
                          The message history was not included with this
                          finding.
                        </p>
                      } @else {
                        <ol>
                          @for (message of messageHistory(item); track $index) {
                            <li>
                              <div>
                                <strong>{{ messageRole(message) }}</strong>
                                @if (messageTimestamp(message)) {
                                  <time>{{ messageTimestamp(message) }}</time>
                                }
                              </div>
                              <p>
                                {{
                                  messageText(message) ||
                                    'No message content available.'
                                }}
                              </p>
                            </li>
                          }
                        </ol>
                      }
                    </section>
                    <section class="finding-details">
                      <h3>Finding details</h3>
                      <dl>
                        <dt>Transcript timestamp</dt>
                        <dd>{{ item.sk | date: 'medium' }} ({{ item.sk }})</dd>
                        <dt>Customer ID</dt>
                        <dd>{{ item.customerId || '—' }}</dd>
                        <dt>Session ID</dt>
                        <dd>{{ item.sessionId || '—' }}</dd>
                        @if (voice) {
                          <dt>Connection ID</dt>
                          <dd>{{ item.connectionId || item.pk || '—' }}</dd>
                          <dt>Voice session ID</dt>
                          <dd>{{ item.voiceSessionId || '—' }}</dd>
                        }
                        <dt>Plan number</dt>
                        <dd>{{ item.planNumber || '—' }}</dd>
                        <dt>Product / brand</dt>
                        <dd>
                          {{ item.productType || '—' }} /
                          {{ item.brand || '—' }}
                        </dd>
                        <dt>Guardrail message</dt>
                        <dd>{{ item.guardrailsMessage ? 'Yes' : 'No' }}</dd>
                        <dt>Chat duration</dt>
                        <dd>
                          {{
                            item.chatDuration ? item.chatDuration + 's' : '—'
                          }}
                        </dd>
                        <dt>Analysis status</dt>
                        <dd>{{ item.analysisStatus || '—' }}</dd>
                        <dt>Analysed at</dt>
                        <dd>
                          {{
                            item.analysedAt
                              ? (item.analysedAt | date: 'medium')
                              : '—'
                          }}
                        </dd>
                        <dt>Review status</dt>
                        <dd>
                          {{
                            item.reviewStatus === 'review-complete'
                              ? 'Review complete'
                              : 'Needs reviewing'
                          }}
                        </dd>
                        @if (item.reviewStatus === 'review-complete') {
                          <dt>Reviewed by</dt>
                          <dd>{{ item.reviewedBy || '—' }}</dd>
                          <dt>Reviewed at</dt>
                          <dd>
                            {{
                              item.reviewedAt
                                ? (item.reviewedAt | date: 'medium')
                                : '—'
                            }}
                          </dd>
                        }
                      </dl>
                    </section>
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  }
</section>
